import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from "react-router-dom";

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS — "Vital Signal" system
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  ink:      '#0A1628',
  ink2:     '#132038',
  canvas:   '#F6F8FC',
  paper:    '#FFFFFF',
  mist:     '#EEF2FA',
  line:     'rgba(10, 22, 40, 0.08)',
  lineFirm: 'rgba(10, 22, 40, 0.14)',
  slate:    '#56637D',
  slateLt:  'rgba(10, 22, 40, 0.55)',
  brand:    '#0F2A5C',
  brandLt:  '#2452A0',
  brandPale:'#E7EDF9',
  signal:   '#0EA383',
  signalDk: '#0B7C64',
  gold:     '#D4A12A',
  danger:   '#C94A3A',
  shadow:   '0 12px 48px rgba(10, 22, 40, 0.07)',
  shadowHover: '0 20px 60px rgba(10, 22, 40, 0.13)',
};

const FONT_DISPLAY = "'Fraunces', serif";
const FONT_BODY = "'Space Grotesk', sans-serif";
const FONT_MONO = "'IBM Plex Mono', monospace";

// ─── Real photography ──────────────────────────────────────────────────────
const IMG = {
  heroHallway:  'https://images.unsplash.com/photo-1517120026326-d87759a7b63b?auto=format&fit=crop&w=1800&q=80',
  laptopDesk:   'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1200&q=80',
  stethoscope:  'https://images.unsplash.com/photo-1521089542882-2e5f5df7cae7?auto=format&fit=crop&w=1400&q=80',
  clinicInterior: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?auto=format&fit=crop&w=1400&q=80',
  waitingRoom: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1400&q=80',
  happyDoctor: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?auto=format&fit=crop&w=1400&q=80',
};

// ─── Signature hero animation ──────────────────────────────────────────────
const VitalCircuit = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width = canvas.width = canvas.parentElement.offsetWidth;
    let height = canvas.height = canvas.parentElement.offsetHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const handleResize = () => {
      width = canvas.parentElement.offsetWidth;
      height = canvas.parentElement.offsetHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildLattice();
    };

    let nodes = [];
    let edges = [];

    function buildLattice() {
      const cols = width < 700 ? 6 : 10;
      const rows = width < 700 ? 5 : 6;
      nodes = [];
      for (let r = 0; r <= rows; r++) {
        for (let c = 0; c <= cols; c++) {
          const jitter = Math.min(width, height) * 0.02;
          nodes.push({
            x: (c / cols) * width + (Math.random() - 0.5) * jitter * 2,
            y: (r / rows) * height + (Math.random() - 0.5) * jitter * 2,
            r: Math.random() * 1.4 + 1.1,
            glow: Math.random() * Math.PI * 2,
          });
        }
      }
      edges = [];
      const cellW = width / cols;
      const cellH = height / rows;
      nodes.forEach((n, i) => {
        nodes.forEach((m, j) => {
          if (i >= j) return;
          const dx = n.x - m.x, dy = n.y - m.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < cellW * 1.5 && d < cellH * 2.2 && Math.random() < 0.55) {
            edges.push({ a: i, b: j, len: d });
          }
        });
      });
    }

    class Pulse {
      constructor() { this.reset(); }
      reset() {
        this.node = Math.floor(Math.random() * nodes.length);
        this.progress = 0;
        this.speed = 0.0022 + Math.random() * 0.0035;
        this.target = this.pickNeighbor(this.node);
        this.hue = Math.random() < 0.78 ? 'signal' : 'gold';
        this.size = 1.6 + Math.random() * 1.6;
      }
      pickNeighbor(from) {
        const opts = edges.filter(e => e.a === from || e.b === from);
        if (!opts.length) return Math.floor(Math.random() * nodes.length);
        const e = opts[Math.floor(Math.random() * opts.length)];
        return e.a === from ? e.b : e.a;
      }
      update() {
        this.progress += this.speed;
        if (this.progress >= 1) {
          this.node = this.target;
          this.target = this.pickNeighbor(this.node);
          this.progress = 0;
        }
      }
      draw(ctx) {
        const from = nodes[this.node], to = nodes[this.target];
        if (!from || !to) return;
        const x = from.x + (to.x - from.x) * this.progress;
        const y = from.y + (to.y - from.y) * this.progress;
        const color = this.hue === 'signal' ? '14,163,131' : '212,161,42';
        const grad = ctx.createRadialGradient(x, y, 0, x, y, this.size * 5);
        grad.addColorStop(0, `rgba(${color},0.55)`);
        grad.addColorStop(1, `rgba(${color},0)`);
        ctx.beginPath();
        ctx.arc(x, y, this.size * 5, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color},0.95)`;
        ctx.fill();
      }
    }

    let pulses = [];
    let ecgPhase = 0;

    function drawECG(baseY) {
      ctx.beginPath();
      const step = 4;
      for (let x = -step; x <= width + step; x += step) {
        const t = (x + ecgPhase) % 260;
        let y = baseY;
        if (t > 40 && t < 46) y -= (t - 40) * 6;
        else if (t >= 46 && t < 52) y += (t - 46) * 14 - 36;
        else if (t >= 52 && t < 58) y -= (t - 52) * 18 - 48;
        else if (t >= 58 && t < 66) y += (t - 58) * 6 - 24;
        if (x === -step) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = 'rgba(14,163,131,0.55)';
      ctx.lineWidth = 1.6;
      ctx.stroke();

      const hx = ((ecgPhase * -1) % width + width) % width;
      const t2 = (hx + ecgPhase) % 260;
      let hy = baseY;
      if (t2 > 40 && t2 < 46) hy -= (t2 - 40) * 6;
      else if (t2 >= 46 && t2 < 52) hy += (t2 - 46) * 14 - 36;
      else if (t2 >= 52 && t2 < 58) hy -= (t2 - 52) * 18 - 48;
      else if (t2 >= 58 && t2 < 66) hy += (t2 - 58) * 6 - 24;
      const g = ctx.createRadialGradient(hx, hy, 0, hx, hy, 10);
      g.addColorStop(0, 'rgba(14,163,131,0.9)');
      g.addColorStop(1, 'rgba(14,163,131,0)');
      ctx.beginPath();
      ctx.arc(hx, hy, 10, 0, Math.PI * 2);
      ctx.fillStyle = g;
      ctx.fill();

      ecgPhase += 0.55;
    }

    buildLattice();
    pulses = Array.from({ length: 9 }, () => new Pulse());
    window.addEventListener('resize', handleResize);

    let raf;
    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      edges.forEach(e => {
        const a = nodes[e.a], b = nodes[e.b];
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = 'rgba(15,42,92,0.08)';
        ctx.lineWidth = 0.6;
        ctx.stroke();
      });
      nodes.forEach(n => {
        n.glow += 0.015;
        const a = 0.25 + 0.2 * Math.sin(n.glow);
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(15,42,92,${a})`;
        ctx.fill();
      });
      pulses.forEach(p => { p.update(); p.draw(ctx); });
      drawECG(height * 0.82);

      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <canvas ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.85 }} />
  );
};

// ─── Inline SVG Icon ──────────────────────────────────────────────────────
const Icon = ({ path, size = 20, color = 'currentColor', strokeWidth = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <path d={path} />
  </svg>
);

const ICONS = {
  arrow:       "M5 12h14M12 5l7 7-7 7",
  check:       "M20 6L9 17l-5-5",
  x:           "M18 6L6 18M6 6l12 12",
  chevronDown: "M6 9l6 6 6-6",
  whatsapp:    "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z",
  ai:          "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
  chart:       "M18 20V10M12 20V4M6 20v-6",
  bell:        "M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0",
  calendar:    "M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z",
  cake:        "M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8",
  pill:        "M10.5 20H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v7.5",
  heart:       "M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z",
  mail:        "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6",
  file:        "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8",
  receipt:     "M14 2H6a2 2 0 0 0-2 2v16l4-2 4 2 4-2 4 2V4a2 2 0 0 0-2-2zM8 10h8M8 14h4",
  star:        "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",
  quote:       "M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1zm12 0c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z",
  shield:      "M12 2 3 6v6c0 5.25 3.75 9.75 9 11 5.25-1.25 9-5.75 9-11V6l-9-4zM8.5 12l2.5 2.5 5-5",
  clock:       "M21 12a9 9 0 1 1-9-9 9 9 0 0 1 9 9zM12 6v6l4 2",
  wifi:        "M5 13a10 10 0 0 1 14 0M8.5 16.5a5 5 0 0 1 7 0M12 20h.01",
  sparkle:     "M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z",
  users:       "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  phone:       "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z",
  activity:    "M22 12h-4l-3 9L9 3l-3 9H2",
  briefcase:   "M20 7h-4V5l-2-2h-4L8 5v2H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zm-8-2h4v2h-4V5z",
  barChart:    "M18 20V10M12 20V4M6 20v-6",
  smile:       "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01",
  trendingUp:  "M2 12l6-6 4 4 8-8M22 4v8h-8",
  shield2:     "M12 2 3 6v6c0 5.25 3.75 9.75 9 11 5.25-1.25 9-5.75 9-11V6l-9-4z",
  zap:         "M13 2L4 14h5l-2 8 9-12h-5z",
  building:    "M21 21H3M4 8.5V21M20 8.5V21M12 3L4 8.5h16L12 3zM8 13h8M8 17h8",
  code:        "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 18l2-2-2-2M14 14l2 2-2 2M12 6v6M9.5 21.5l3-3",
  menu:        "M3 12h18M3 6h18M3 18h18",
  close:       "M6 18L18 6M6 6l12 12",
};

// ─── Content ──────────────────────────────────────────────────────────────
const FEATURE_GROUPS = [
  {
    label: 'Patient Care',
    items: [
      { icon: 'file',     title: 'Electronic Health Records',  desc: "Every patient's history, one search away — no more digging through paper files." },
      { icon: 'heart',    title: 'Antenatal Auto-Detection',    desc: 'Flags mothers due for a scan or ANC visit before they fall through the cracks.' },
      { icon: 'pill',     title: 'Drug Suggestions',            desc: 'Surfaces the right prescription options based on diagnosis, in seconds.' },
      { icon: 'ai',       title: 'AI Report Writing',           desc: 'Drafts ultrasound and clinical reports for your doctor to review and sign.' },
      { icon: 'receipt',  title: 'Instant Financial Records Update',    desc: "All your financial records are updated instantly. You also receive an SMS alert of your clinic's daily financial performance." },
      { icon: 'receipt',  title: 'Instant Patient Receipts',    desc: "A receipt lands on the patient's phone the moment they pay." },
      { icon: 'bell',     title: 'Debt Reminder Automation',    desc: 'Sends the balance-due SMS, so your front desk never has to.' },
      { icon: 'calendar', title: 'Appointment SMS Alerts',      desc: 'Cuts no-shows with a reminder sent before every booked visit.' },
      { icon: 'cake',     title: 'Birthday Greetings',          desc: 'Sends a personal birthday message automatically — patients notice.' },
      { icon: 'mail',     title: 'Targeted Bulk Messaging',     desc: 'Reach every patient, or just one group, in a single click.' },
      { icon: 'ai',       title: 'AI Employee Assessment',      desc: "Shows you who's performing and exactly where training is needed." },
      { icon: 'chart',    title: 'District Report Generation',  desc: 'Builds reports in the exact format your district health office expects and submits it automatically.' },
    ],
  },
];

const STEPS = [
  { n: '01', title: 'Register your clinic',   desc: 'Create your account and add your team. Takes under 3 minutes.' },
  { n: '02', title: 'Set it up', desc: 'Set up your drug prices, lab tests, and procedures — each with its own price.' },
  { n: '03', title: 'Go live',                desc: 'Reminders, billing, and reports start working automatically from day one.' },
];

const TESTIMONIALS = [
  { name: "Madam Getrude", role: "LIFE-SURE MEDICARE",   initial: "G", text: "ClinicPro transformed our clinic management. Tracking appointments, sales and expenses has never been easier." },
  { name: "Madam Sarah",   role: "Kikajjo Health Center", initial: "S", text: "The automated reminders and AI-powered features have saved us so much time and effort!" },
  { name: "Doctor Joshua", role: "Asaba Medical Clinic",  initial: "J", text: "A fantastic tool that made our clinic run more smoothly. I highly recommend it to every clinic owner." },
];

const TRANSFORMATIONS = [
  "Your waiting room changes from chaos to calm with automated check-ins",
  "Patient anxiety drops — they see exactly when they'll be seen",
  "Your staff shifts from stressed multitaskers to focused caregivers",
  "Paper clutter disappears — everything lives in one smart system",
  "Follow-ups become automatic, not a burden on your team",
  "Your revenue flows predictably — no more forgotten payments",
  "Prescriptions move from scribbled notes to digital clarity",
  "Patient history becomes instantly accessible, not a treasure hunt",
  "Your brand transforms from 'good' to 'exceptional' in patient eyes",
  "Staff morale improves — they feel supported, not overwhelmed",
  "No-shows become rare, not the norm, with smart reminders",
  "Your evenings change — you actually leave on time, without guilt",
  "Patient loyalty deepens — they recommend you to everyone",
  "Your clinic goes from reactive to proactive in patient care",
  "Team communication evolves — no more yelling across the hall",
  "Your reports generate themselves — decisions get easier",
  "Patients stop asking 'how much?' — transparency builds trust",
  "Your clinic grows without chaos — scale becomes manageable",
  "Stress levels drop — you run your practice, it doesn't run you",
  "Your smile returns — you remember why you became a doctor"
];

const FAQS = [
  { q: 'Is ClinicProSystem free to start?', a: 'No. You can however see how it works by logging in using demo details — Clinic name: SAMPLE CLINIC, Password: 12345, Employee name: CLEDD, Login code: 1. After you feel satisfied, you can register your clinic and login, you will be asked to pay the initial activation fee of ugx 150000.  After activation you can start. You will after a few days have to choose your subscription plan. Our lowest subscription plan is as low as 21,000 UGX per month.' },
  { q: "What if our clinic doesn't have reliable internet?", a: 'The dashboard is built to run on low bandwidth. Most clinics run comfortably on an ordinary phone hotspot.' },
  { q: 'Is our patient data secure?', a: 'Records are encrypted and access is restricted by staff role, so a receptionist and a doctor see only what their role allows. Backups run automatically, every day.Your data is stored on global servers that have multiple back-ups across the world. This is actually why you can be able to access your data quickly anytime anywhere' },
  { q: 'Cant i pay once and for all or why do u always ask for a subscription?', a: "Our system , just like all good classic software, it is always mantained and updated depending on your user experience as a customer, this means it will always require a timely funding to enable  us support you. Additionally the servers onto which your data is stored always need maintance and updates as well as backup of data which will as well require a monthly funding. All these and other left out necessities push us to get a funding from you to keep us serving you perfectly  " },
  { q: 'Can it work on phones?', a: "Yes it does. It actually works better if u use chrome or any browser that has the desktop view feature . Use the desktop view" },
  { q: 'Cant i use it offline?', a: "This will require you to set up your own server in your clinic or hospital so u can acess your data without the need of an internet connection. We can always do this for you at a  fee of ugx 600,000 and above. The charge entirely depends on the size of your clinic " },
  { q: 'What makes this system unique?', a: "2 features in this system that are so unique. One is its ability to connect directly to the ministry of health servers in case you need to submit your data. Secondly is its artificial intelligence engine that sorts and organises data and also gives assistance on prescriptions and also sends sms to patients to remind them of appointments which were created by the Artificial intelligence its self. Trust us u need this system! Its one of a kind ." },
];

const EASY_FEATURES = [
  { icon: 'wifi',    title: 'Runs on what you already have', desc: 'Works on an ordinary phone, laptop or shared computer, over a normal data bundle or clinic Wi-Fi. No special hardware to buy.' },
  { icon: 'clock',   title: 'Live in under 10 minutes',      desc: 'No onboarding project, no consultants. Register, add your team, and your clinic is running the same day.' },
  { icon: 'shield',  title: 'No IT staff required',          desc: 'If your team can use WhatsApp, they can use ClinicPro. Anything trickier is one message away from our Uganda-based support team.' },
  { icon: 'whatsapp',title: 'Support in your pocket',        desc: 'Real people on WhatsApp, not a ticket queue — for setup questions, training, or just double-checking a report.' },
];

// ─── CPS Logo ──────────────────────────────────────────────────────────────
const CPSLogo = ({ size = 36, dark = false }) => (
  <div style={{
    width: size, height: size, borderRadius: size * 0.28,
    background: dark ? T.ink : T.brand,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative', overflow: 'hidden', flexShrink: 0,
    boxShadow: dark ? '0 4px 16px rgba(0,0,0,0.1)' : '0 4px 16px rgba(15, 42, 92, 0.25)',
  }}>
    <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: size * 0.4, color: '#fff', letterSpacing: '-0.5px', lineHeight: 1, userSelect: 'none' }}>CPS</span>
    <div style={{ position: 'absolute', bottom: size * 0.1, right: size * 0.1, width: size * 0.14, height: size * 0.14, borderRadius: '50%', background: T.signal, opacity: 0.95 }} />
  </div>
);

// ─── Ambient Background ──────────────────────────────────────────────────
const AmbientBg = ({ light = false }) => (
  <div className="particle-field">
    {[...Array(20)].map((_, i) => (
      <div key={i} className="particle" style={{
        width: Math.random() * 4 + 1.5,
        height: Math.random() * 4 + 1.5,
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animationDuration: `${Math.random() * 25 + 12}s`,
        animationDelay: `${Math.random() * 12}s`,
        background: light
          ? `rgba(15, 42, 92, ${Math.random() * 0.08 + 0.04})`
          : `rgba(255, 255, 255, ${Math.random() * 0.15 + 0.05})`,
      }} />
    ))}
  </div>
);

// ─── Image Fade Section ──────────────────────────────────────────────────
const ImageFadeSection = ({ image, children, height = '60vh', position = 'center' }) => (
  <div style={{ position: 'relative', height, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <img src={image} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: position }} />
    <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, ${T.canvas} 0%, rgba(246, 248, 252, 0.3) 30%, rgba(246, 248, 252, 0.1) 50%, rgba(246, 248, 252, 0.3) 70%, ${T.canvas} 100%)` }} />
    <div style={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </div>
  </div>
);

// ─── Hooks ────────────────────────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { 
      if (e.isIntersecting) setInView(true); 
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function useCounter(target, duration = 2000, active = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const tick = ts => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setVal(Math.round(p * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [active, target, duration]);
  return val;
}

function useRevealProgress(duration = 2600, delay = 300) {
  const [p, setP] = useState(0);
  useEffect(() => {
    let raf; let start = null;
    const tick = ts => {
      if (!start) start = ts;
      const t = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setP(eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    const to = setTimeout(() => { raf = requestAnimationFrame(tick); }, delay);
    return () => { clearTimeout(to); if (raf) cancelAnimationFrame(raf); };
  }, [duration, delay]);
  return p;
}

// ─── Reusable Buttons ────────────────────────────────────────────────────
const PrimaryButton = ({ children, onClick, small = false }) => (
  <button onClick={onClick}
    style={{
      padding: small ? '12px 24px' : '16px 34px', borderRadius: 10, border: 'none',
      background: `linear-gradient(135deg, ${T.brand}, ${T.brandLt})`,
      color: '#fff', fontWeight: 700, fontSize: small ? 13 : 15,
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
      fontFamily: FONT_BODY, boxShadow: `0 8px 32px rgba(15, 42, 92, 0.22)`,
      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(15, 42, 92, 0.32)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(15, 42, 92, 0.22)'; }}>
    {children}
  </button>
);

const GhostButton = ({ children, onClick, small = false }) => (
  <button onClick={onClick}
    style={{
      padding: small ? '12px 22px' : '16px 30px', borderRadius: 10, border: `1.5px solid ${T.lineFirm}`,
      background: 'rgba(255,255,255,0.8)', color: T.ink, fontWeight: 700, fontSize: small ? 13 : 15,
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
      fontFamily: FONT_BODY, backdropFilter: 'blur(10px)', transition: 'all 0.3s',
    }}
    onMouseEnter={e => { e.currentTarget.style.background = T.paper; e.currentTarget.style.borderColor = T.signal; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = T.shadowHover; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.8)'; e.currentTarget.style.borderColor = T.lineFirm; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
    {children}
  </button>
);

// ─── Feature Card ──────────────────────────────────────────────────────
function FeatureCard({ icon, title, desc, index }) {
  const [hov, setHov] = useState(false);
  const [ref, inView] = useInView(0.1);
  const accent = index % 3 === 0 ? T.signal : T.brand;
  return (
    <div ref={ref} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? T.paper : T.mist, borderRadius: 16, padding: '28px 24px',
        border: `1px solid ${hov ? accent : 'transparent'}`, cursor: 'default',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
        transition: `all 0.6s ${index * 0.05}s cubic-bezier(0.34, 1.56, 0.64, 1)`,
        height: '100%', boxShadow: hov ? T.shadowHover : 'none',
      }}>
      <div style={{ width: 44, height: 44, borderRadius: 11, background: hov ? accent : T.brandPale, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18, color: hov ? '#fff' : accent, transition: 'all 0.3s' }}>
        <Icon path={ICONS[icon]} size={20} />
      </div>
      <div style={{ fontSize: 15.5, fontWeight: 700, marginBottom: 8, color: T.ink, fontFamily: FONT_DISPLAY }}>{title}</div>
      <div style={{ fontSize: 13.5, color: T.slate, lineHeight: 1.65 }}>{desc}</div>
    </div>
  );
}

// ─── DHIS2 Step Card ──────────────────────────────────────────────────
function DHIS2StepCard({ icon, title, desc, index }) {
  const [hov, setHov] = useState(false);
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? T.paper : T.mist, borderRadius: 16, padding: '24px 20px',
        border: `1px solid ${hov ? T.brand : 'transparent'}`, cursor: 'default',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
        transition: `all 0.6s ${index * 0.05}s cubic-bezier(0.34, 1.56, 0.64, 1)`,
        boxShadow: hov ? T.shadowHover : 'none', textAlign: 'center',
      }}>
      <div style={{ width: 48, height: 48, borderRadius: '50%', background: hov ? T.brand : T.brandPale, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: hov ? '#fff' : T.brand, transition: 'all 0.3s' }}>
        <Icon path={ICONS[icon]} size={22} />
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 8, fontFamily: FONT_DISPLAY }}>{title}</div>
      <div style={{ fontSize: 13, color: T.slate, lineHeight: 1.6 }}>{desc}</div>
    </div>
  );
}

// ─── FAQ Item ──────────────────────────────────────────────────────────
function FAQItem({ q, a, open, onToggle }) {
  return (
    <div style={{ borderBottom: `1px solid ${T.line}`, background: open ? T.brandPale : 'transparent', transition: 'background 0.3s' }}>
      <button onClick={onToggle} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, padding: '22px 4px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: T.ink, fontFamily: FONT_DISPLAY }}>{q}</span>
        <span style={{ flexShrink: 0, width: 28, height: 28, borderRadius: '50%', background: open ? T.brand : T.mist, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s', transform: open ? 'rotate(180deg)' : 'none' }}>
          <Icon path={ICONS.chevronDown} size={15} color={open ? '#fff' : T.slate} />
        </span>
      </button>
      <div style={{ maxHeight: open ? 200 : 0, overflow: 'hidden', transition: 'max-height 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
        <p style={{ fontSize: 14.5, color: T.slate, lineHeight: 1.75, padding: '0 4px 24px' }}>{a}</p>
      </div>
    </div>
  );
}

// ─── Easy Feature Card ────────────────────────────────────────────────
function EasyFeatureCard({ icon, title, desc, index, inView }) {
  const [hover, setHover] = useState(false);
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? T.paper : T.mist, border: `1px solid ${hover ? T.signal : 'transparent'}`,
        borderRadius: 16, padding: '26px 22px',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
        transition: `all 0.6s ${index * 0.08}s cubic-bezier(0.34, 1.56, 0.64, 1)`,
        boxShadow: hover ? T.shadowHover : 'none',
      }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: hover ? T.signal : T.brandPale, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, color: hover ? '#fff' : T.brand, transition: 'background 0.3s' }}>
        <Icon path={ICONS[icon]} size={18} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, marginBottom: 8, fontFamily: FONT_DISPLAY }}>{title}</div>
      <div style={{ fontSize: 13, color: T.slate, lineHeight: 1.65 }}>{desc}</div>
    </div>
  );
}

// ─── Step Card ──────────────────────────────────────────────────────
function StepCard({ step, index, inView }) {
  return (
    <div style={{
      position: 'relative', padding: '30px 24px', background: T.paper, borderRadius: 16,
      border: `1.5px solid ${T.line}`,
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
      transition: `all 0.6s ${index * 0.1}s cubic-bezier(0.34, 1.56, 0.64, 1)`,
      boxShadow: T.shadow,
    }}>
      <div style={{ fontFamily: FONT_MONO, fontSize: 13, fontWeight: 700, color: T.signal, marginBottom: 18 }}>{step.n}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color: T.ink, marginBottom: 10, fontFamily: FONT_DISPLAY }}>{step.title}</div>
      <div style={{ fontSize: 14, color: T.slate, lineHeight: 1.7 }}>{step.desc}</div>
    </div>
  );
}

// ─── Value Item ────────────────────────────────────────────────────────
function ValueItem({ text, index, inView }) {
  const [hover, setHover] = useState(false);
  return (
    <div 
      onMouseEnter={() => setHover(true)} 
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', gap: 12, alignItems: 'flex-start',
        padding: '14px 18px',
        borderRadius: 10,
        background: hover ? 'rgba(14, 163, 131, 0.06)' : 'transparent',
        transition: 'background 0.3s ease',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateX(0) scale(1)' : 'translateX(-20px) scale(0.97)',
        transition: `all 0.5s ${index * 0.03}s cubic-bezier(0.34, 1.56, 0.64, 1)`,
        cursor: 'default',
      }}>
      <div style={{ width: 24, height: 24, borderRadius: 6, background: hover ? `linear-gradient(135deg, ${T.signal}, ${T.signalDk})` : T.signal, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, transition: 'background 0.3s' }}>
        <Icon path={ICONS.check} size={12} color="#fff" />
      </div>
      <span style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.55, fontWeight: 500 }}>{text}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN LANDING PAGE
// ═══════════════════════════════════════════════════════════════════════════
const LandingPage = () => {
  const navigate = useNavigate();
  const [activeT, setActiveT] = useState(0);
  const [navScrolled, setNavScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [heroVisible, setHeroVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const heroRef = useRef(null);
  const revealProgress = useRevealProgress();
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [hoveringHero, setHoveringHero] = useState(false);
  const [tiltProgress, setTiltProgress] = useState(0);

  const [statsRef, statsInView] = useInView(0.3);
  const [valRef, valInView] = useInView(0.1);

  const [easyRef1, easyInView1] = useInView(0.1);
  const [easyRef2, easyInView2] = useInView(0.1);
  const [easyRef3, easyInView3] = useInView(0.1);
  const [easyRef4, easyInView4] = useInView(0.1);
  const easyInViews = [easyInView1, easyInView2, easyInView3, easyInView4];
  const easyRefs = [easyRef1, easyRef2, easyRef3, easyRef4];

  const [stepRef1, stepInView1] = useInView(0.1);
  const [stepRef2, stepInView2] = useInView(0.1);
  const [stepRef3, stepInView3] = useInView(0.1);
  const stepInViews = [stepInView1, stepInView2, stepInView3];
  const stepRefs = [stepRef1, stepRef2, stepRef3];

  const clinics  = useCounter(120,   1800, statsInView);
  const patients = useCounter(50000, 2200, statsInView);
  const speed    = useCounter(80,    1600, statsInView);

  useEffect(() => { setHeroVisible(true); }, []);
  useEffect(() => {
    const t = setInterval(() => setActiveT(i => (i + 1) % TESTIMONIALS.length), 5500);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const fn = () => setNavScrolled(window.scrollY > 30);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        if (heroRef.current) {
          const h = heroRef.current.offsetHeight || 1;
          const p = Math.min(Math.max(window.scrollY / h, 0), 1);
          setTiltProgress(p);
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleHeroMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const openDemo = () => window.open("https://wa.me/256703521101?text=Hi!%20I'm%20interested%20in%20a%20demo.", "_blank");
  const register = () => navigate('/clinic-registration');

  const handleMobileNav = (fn) => {
    setMobileMenuOpen(false);
    fn();
  };

  return (
    <div style={{ 
      fontFamily: FONT_BODY, 
      background: T.canvas, 
      color: T.ink, 
      overflowX: 'hidden',
      minHeight: '100vh',
      marginTop: 0,
      paddingTop: 0,
    }}>
      <Helmet />

      {/* ══ NAV ══ */}
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200, height: 68,
        background: navScrolled ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(20px)', display: 'flex', alignItems: 'center', padding: '0 5vw',
        borderBottom: 'none',
        transition: 'all 0.4s',
        boxShadow: navScrolled ? T.shadow : 'none',
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          width: '100%', 
          maxWidth: 1200, 
          margin: '0 auto' 
        }}>
          {/* Logo pushed to the edge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 0 }}>
            <CPSLogo size={36} dark />
            <div style={{ fontSize: 19, fontWeight: 800, color: T.ink, letterSpacing: '-0.4px', fontFamily: FONT_DISPLAY, lineHeight: 1.1 }}>
              ClinicPro<span style={{ color: T.brand }}>System</span>
            </div>
          </div>
          
          {/* Desktop Navigation - pushed to the right edge */}
          <nav style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 0 }}>
            <button onClick={() => navigate('/login')}
              style={{ background: 'none', border: `1.5px solid ${T.line}`, color: T.ink, fontWeight: 700, fontSize: 14, cursor: 'pointer', padding: '8px 18px', borderRadius: 8, marginLeft: 4, fontFamily: FONT_BODY, transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = T.brandPale; e.currentTarget.style.borderColor = T.brand; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = T.line; }}>
              Log in
            </button>
            <button onClick={register}
              style={{ background: `linear-gradient(135deg, ${T.brand}, ${T.brandLt})`, border: 'none', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer', padding: '9px 20px', borderRadius: 8, marginLeft: 4, fontFamily: FONT_BODY, boxShadow: '0 4px 20px rgba(15, 42, 92, 0.25)', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(15, 42, 92, 0.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(15, 42, 92, 0.25)'; }}>
              Register Free
            </button>
          </nav>

          {/* Mobile Hamburger */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: 8,
              transition: 'all 0.2s',
              color: T.ink,
            }}
            className="mobile-hamburger"
          >
            <Icon path={mobileMenuOpen ? ICONS.close : ICONS.menu} size={24} />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div style={{
          position: 'fixed',
          top: 68,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          zIndex: 199,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 24,
          padding: '40px 5vw',
          animation: 'slideDown 0.3s ease-out',
        }}>
          <button 
            onClick={() => handleMobileNav(() => navigate('/login'))}
            style={{
              background: 'none',
              border: `1.5px solid ${T.line}`,
              color: T.ink,
              fontWeight: 700,
              fontSize: 18,
              cursor: 'pointer',
              padding: '14px 40px',
              borderRadius: 12,
              width: '100%',
              maxWidth: 280,
              fontFamily: FONT_BODY,
              transition: 'all 0.2s',
            }}
          >
            Log in
          </button>
          <button 
            onClick={() => handleMobileNav(register)}
            style={{
              background: `linear-gradient(135deg, ${T.brand}, ${T.brandLt})`,
              border: 'none',
              color: '#fff',
              fontWeight: 800,
              fontSize: 18,
              cursor: 'pointer',
              padding: '14px 40px',
              borderRadius: 12,
              width: '100%',
              maxWidth: 280,
              fontFamily: FONT_BODY,
              boxShadow: '0 4px 20px rgba(15, 42, 92, 0.25)',
              transition: 'all 0.2s',
            }}
          >
            Register Free
          </button>
          <button 
            onClick={() => handleMobileNav(openDemo)}
            style={{
              background: 'none',
              border: `1.5px solid ${T.signal}`,
              color: T.signal,
              fontWeight: 700,
              fontSize: 18,
              cursor: 'pointer',
              padding: '14px 40px',
              borderRadius: 12,
              width: '100%',
              maxWidth: 280,
              fontFamily: FONT_BODY,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'all 0.2s',
            }}
          >
            <Icon path={ICONS.whatsapp} size={18} color={T.signal} /> Book a Demo
          </button>
        </div>
      )}

      {/* ══ HERO ══ */}
      <section ref={heroRef}
        onMouseMove={handleHeroMouseMove}
        onMouseEnter={() => setHoveringHero(true)}
        onMouseLeave={() => setHoveringHero(false)}
        style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', padding: '130px 5vw 0', position: 'relative', overflow: 'hidden',
          transformOrigin: '50% 100%', transformStyle: 'preserve-3d',
          transform: `perspective(1500px) rotateX(-${tiltProgress * 68}deg) translateY(${tiltProgress * 34}px) scale(${1 - tiltProgress * 0.06})`,
          willChange: 'transform',
        }}>
        <img src={IMG.heroHallway} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: `
            radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 20%, rgba(255,255,255,0.3) 45%, rgba(10, 22, 40, 0.4) 60%, rgba(10, 22, 40, 0.72) 80%, rgba(10, 22, 40, 0.92) 100%),
            linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.4) 40%, rgba(10, 22, 40, 0.5) 70%, rgba(10, 22, 40, 0.82) 100%)
          `,
        }} />

        <VitalCircuit />

        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.03, pointerEvents: 'none' }} xmlns="http://www.w3.org/2000/svg">
          <defs><pattern id="dots" width="30" height="30" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1.2" fill="white" /></pattern></defs>
          <rect width="100%" height="100%" fill="url(#dots)" />
        </svg>

        <div style={{
          position: 'absolute', inset: 0, zIndex: 2, background: T.ink, pointerEvents: 'none',
          WebkitMaskImage: `radial-gradient(circle at 50% 38%, transparent ${revealProgress * 100}%, black ${revealProgress * 100 + 14}%)`,
          maskImage: `radial-gradient(circle at 50% 38%, transparent ${revealProgress * 100}%, black ${revealProgress * 100 + 14}%)`,
        }} />

        <div style={{
          position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none',
          opacity: hoveringHero ? 1 : 0, transition: 'opacity 0.4s ease',
          background: `radial-gradient(circle 260px at ${mousePos.x}px ${mousePos.y}px, rgba(255,255,255,0.30), rgba(255,255,255,0.08) 45%, transparent 72%)`,
        }} />

        <div style={{ position: 'relative', zIndex: 4, maxWidth: 900, opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.97)', transition: 'all 1s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', border: '1px solid rgba(15, 42, 92, 0.08)', borderRadius: 100, padding: '7px 20px', fontSize: 12, color: T.slate, marginBottom: 32, fontFamily: FONT_MONO, letterSpacing: 0.4, boxShadow: '0 2px 20px rgba(0,0,0,0.06)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.signal, display: 'inline-block', animation: 'pulseDot 2s infinite' }} />
            THIS IS THE TIME TO UPGRADE YOUR CLINIC BUSINESS.
          </div>

          <h1 style={{ fontSize: 'clamp(2.8rem, 7vw, 5.5rem)', fontWeight: 700, lineHeight: 1.05, letterSpacing: '-2px', color: T.ink, margin: '0 0 24px', fontFamily: FONT_DISPLAY, textShadow: '0 2px 40px rgba(255,255,255,0.3)' }}>
            The <span style={{ color: T.brand }}>AI-Powered</span> Clinic<br />
            <span style={{ fontStyle: 'italic', fontWeight: 600, color: T.brandLt }}>Management System</span>
          </h1>

          <p style={{ fontSize: 'clamp(1.1rem, 1.6vw, 1.25rem)', color: T.slate, maxWidth: 600, margin: '0 auto 44px', lineHeight: 1.8, background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(8px)', padding: '12px 24px', borderRadius: 12, display: 'inline-block' }}>
            Stop struggling with paperwork, lost records, stock confusion, financial confusion, and patient no-shows. ClinicProSystem automates records, billing, stock tracking, pharmacy, finances, patient receipts and invoices , doctors room , lab, etc with direct DHIS2 integration for the Ministry of Health.
          </p>

          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <PrimaryButton onClick={register}><Icon path={ICONS.arrow} size={18} color="#fff" /> REGISTER NOW</PrimaryButton>
            <GhostButton onClick={openDemo}><Icon path={ICONS.whatsapp} size={18} color={T.signal} /> Book a DEMO</GhostButton>
          </div>

          <div style={{ display: 'flex', gap: 26, justifyContent: 'center', flexWrap: 'wrap', marginTop: 48 }}>
            {['Setup in 10 minutes', 'Free onboarding support'].map(t => (
              <span key={t} style={{ fontSize: 13, color: T.slate, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.7)', padding: '4px 16px', borderRadius: 20, backdropFilter: 'blur(4px)' }}>
                <Icon path={ICONS.check} size={13} color={T.signal} /> {t}
              </span>
            ))}
          </div>
        </div>

        <div style={{
          position: 'relative', zIndex: 4, marginTop: 76, display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
          width: '100%', maxWidth: 980, background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(16px)',
          overflow: 'hidden', border: '1px solid rgba(255,255,255,0.3)', boxShadow: '0 8px 48px rgba(0,0,0,0.08)',
          opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'all 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s',
          borderRadius: 0,
        }}>
          {[['120+','Clinics Onboarded'],['50K+','Patients Managed'],['80%','Faster Documentation'],['4.9 / 5','Client Rating']].map(([n,l], idx) => (
            <div key={l} style={{ padding: '26px 16px', textAlign: 'center', borderRight: idx < 3 ? '1px solid rgba(15,42,92,0.06)' : 'none' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: T.brand, fontFamily: FONT_MONO, letterSpacing: '-0.5px' }}>{n}</div>
              <div style={{ fontSize: 10.5, color: T.slate, marginTop: 6, textTransform: 'uppercase', letterSpacing: 1.3, fontWeight: 600 }}>{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ FEATURES ══ */}
      <section style={{ padding: 'clamp(80px, 10vw, 120px) 5vw', background: T.mist, borderTop: `1px solid ${T.line}` }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: T.brand, textTransform: 'uppercase', marginBottom: 14, fontFamily: FONT_MONO }}>Time to transform your clinic</div>
            <h2 style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-1px', margin: 0, fontFamily: FONT_DISPLAY, color: T.ink }}>
              Everything your clinic needs,<br /><span style={{ color: T.brand }}>in one system</span>
            </h2>
            <p style={{ color: T.slate, fontSize: 15.5, maxWidth: 520, margin: '16px auto 0' }}>
              Your Financial Records, Stock Tracking, Patient Records, Appointments, Ministry of Health Auto Reporting — all in one place.
            </p>
          </div>

          {FEATURE_GROUPS.map((group, gi) => (
            <div key={group.label} style={{ marginBottom: gi === FEATURE_GROUPS.length - 1 ? 0 : 60 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.brand, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 20, paddingLeft: 2, fontFamily: FONT_MONO }}>{group.label}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
                {group.items.map((f, i) => <FeatureCard key={f.title} {...f} index={i} />)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ DHIS2 INTEGRATION ══ */}
      <section style={{ padding: 'clamp(80px, 10vw, 120px) 0', background: T.paper, borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 5vw' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: T.brand, textTransform: 'uppercase', marginBottom: 14, fontFamily: FONT_MONO }}>Integrated</div>
              <h2 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-1px', margin: '0 0 20px', fontFamily: FONT_DISPLAY, color: T.ink }}>
                Auto-Submit to <br /><span style={{ color: T.brand }}>Ministry of Health</span>
              </h2>
              <p style={{ color: T.slate, fontSize: 15.5, lineHeight: 1.8, marginBottom: 24 }}>
                Every patient visit, diagnosis, and prescription is automatically formatted and submitted to the DHIS2 e-register system. No manual entry, no double work, no errors.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {['Live data sync with DHIS2', 'Ministry-approved reporting formats', 'Real-time validation & error checking', 'Works on any device, anywhere'].map(item => (
                  <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 12, color: T.ink, fontSize: 14 }}>
                    <Icon path={ICONS.check} size={16} color={T.signal} />{item}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ background: `linear-gradient(145deg, ${T.brand}, ${T.brandLt})`, borderRadius: 20, padding: 40, color: '#fff', boxShadow: '0 20px 60px rgba(15, 42, 92, 0.15)' }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                <Icon path={ICONS.shield} size={26} color="#fff" />
              </div>
              <h3 style={{ color: '#fff', fontSize: 20, marginBottom: 12, fontFamily: FONT_DISPLAY }}>How It Works</h3>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, lineHeight: 1.8, marginBottom: 20 }}>
                The integration works seamlessly in the background — your team focuses on patients while ClinicPro handles the reporting.
              </p>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 60 }}>
          <ImageFadeSection image={IMG.clinicInterior} height="50vh" position="center">
            <div style={{ maxWidth: 1000, width: '100%', padding: '0 5vw' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
                {[
                  { icon: 'users', title: 'Patient visit recorded', desc: 'Your team logs the visit as they normally would' },
                  { icon: 'file', title: 'Auto-formatted for DHIS2', desc: 'Data is structured to Ministry standards' },
                  { icon: 'check', title: 'Submitted & confirmed', desc: 'Directly sent to the Ministry system' },
                ].map((item, idx) => <DHIS2StepCard key={idx} {...item} index={idx} />)}
              </div>
            </div>
          </ImageFadeSection>
        </div>

        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '60px 5vw 0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30 }}>
            <div style={{ background: T.mist, border: `1px solid ${T.line}`, borderRadius: 16, padding: 32 }}>
              <div style={{ width: 44, height: 44, borderRadius: 11, background: T.brandPale, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                <Icon path={ICONS.receipt} size={20} color={T.brand} />
              </div>
              <h3 style={{ color: T.ink, fontSize: 20, marginBottom: 8, fontFamily: FONT_DISPLAY }}>Affordable for Every Clinic</h3>
              <p style={{ color: T.slate, fontSize: 14, lineHeight: 1.7 }}>
                <span style={{ color: T.gold, fontWeight: 700, fontSize: 18 }}>Free</span> to register.
                Premium plans from <span style={{ color: T.gold, fontWeight: 700 }}>UGX 21,000/month</span>,
                less than the cost of one missed patient visit.
              </p>
              <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['Affordable Packages',  'Flexible Payment'].map(tag => (
                  <span key={tag} style={{ background: T.paper, padding: '4px 14px', borderRadius: 12, fontSize: 12, color: T.slate, border: `1px solid ${T.line}` }}>{tag}</span>
                ))}
              </div>
            </div>

            <div style={{ background: T.mist, border: `1px solid ${T.line}`, borderRadius: 16, padding: 32 }}>
              <div style={{ width: 44, height: 44, borderRadius: 11, background: T.brandPale, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                <Icon path={ICONS.heart} size={20} color={T.brand} />
              </div>
              <h3 style={{ color: T.ink, fontSize: 20, marginBottom: 8, fontFamily: FONT_DISPLAY }}>Designed for Real People</h3>
              <p style={{ color: T.slate, fontSize: 14, lineHeight: 1.7 }}>
                No IT skills needed. The interface is built for nurses, receptionists, and doctors — with large buttons, clear labels, and instant feedback.
              </p>
              <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['Mobile First', '10-min Setup', 'Intuitive UI'].map(tag => (
                  <span key={tag} style={{ background: T.paper, padding: '4px 14px', borderRadius: 12, fontSize: 12, color: T.slate, border: `1px solid ${T.line}` }}>{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ WHY IT'S EASY ══ */}
      <section style={{ padding: 'clamp(80px, 10vw, 120px) 5vw', background: T.mist, position: 'relative', overflow: 'hidden', borderTop: `1px solid ${T.line}` }}>
        <AmbientBg light />
        <div style={{ maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 48, alignItems: 'center', marginBottom: 52 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: T.brand, textTransform: 'uppercase', marginBottom: 14, fontFamily: FONT_MONO }}>WHY IT'S EASY</div>
              <h2 style={{ fontSize: 'clamp(1.8rem, 3.2vw, 2.6rem)', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-1px', margin: '0 0 16px', fontFamily: FONT_DISPLAY, color: T.ink }}>
                Built for a busy front desk, <span style={{ fontStyle: 'italic', color: T.brand }}>priced for a real clinic</span>
              </h2>
              <p style={{ color: T.slate, fontSize: 15.5, lineHeight: 1.75 }}>
                No IT department, no long contracts, no surprise invoices. ClinicProSystem is designed so the person at reception and the doctor in the exam room can both run it — and priced so a small clinic can actually afford it.
              </p>
            </div>
            <div style={{ position: 'relative', borderRadius: 18, overflow: 'hidden', aspectRatio: '4/3', border: `1px solid ${T.line}`, boxShadow: T.shadow }}>
              <img src={IMG.stethoscope} alt="Stethoscope on a clinic desk" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 18, marginBottom: 44 }}>
            {EASY_FEATURES.map((feature, i) => (
              <div key={feature.title} ref={easyRefs[i]}>
                <EasyFeatureCard {...feature} index={i} inView={easyInViews[i]} />
              </div>
            ))}
          </div>

          <div style={{ background: T.paper, border: `1px solid ${T.line}`, borderRadius: 18, padding: 'clamp(24px, 4vw, 36px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20, boxShadow: T.shadow }}>
            <div style={{ maxWidth: 480 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.signal, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 10, fontFamily: FONT_MONO }}>What it costs</div>
              <p style={{ fontSize: 14.5, color: T.slate, lineHeight: 1.75, margin: 0 }}>
                Registration is completely free. Account Activation is ugx 150,000.  Our lowest subscription package is 21,000 UGX per month.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ HOW IT WORKS ══ */}
      <section style={{ padding: 'clamp(80px, 10vw, 120px) 5vw', background: T.paper, borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}` }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: T.brand, textTransform: 'uppercase', marginBottom: 14, fontFamily: FONT_MONO }}>GETTING STARTED</div>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.2vw, 2.6rem)', fontWeight: 700, letterSpacing: '-1px', margin: 0, fontFamily: FONT_DISPLAY, color: T.ink }}>Live in <span style={{ color: T.brand }}>three steps</span></h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
            {STEPS.map((step, i) => (
              <div key={step.n} ref={stepRefs[i]}>
                <StepCard step={step} index={i} inView={stepInViews[i]} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ STATS ══ */}
      <section style={{ position: 'relative', overflow: 'hidden' }}>
        <img src={IMG.waitingRoom} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }} />
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, ${T.brand} 0%, ${T.brandLt} 50%, ${T.brand} 100%)`, opacity: 0.92 }} />
        <div ref={statsRef} style={{ position: 'relative', zIndex: 1, padding: 'clamp(70px, 9vw, 100px) 5vw', textAlign: 'center' }}>
          <AmbientBg />
          <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', marginBottom: 14, fontFamily: FONT_MONO }}>Our Impact</div>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.2vw, 2.6rem)', fontWeight: 700, color: '#fff', letterSpacing: '-1px', marginBottom: 56, fontFamily: FONT_DISPLAY }}>
              Numbers that tell the <span style={{ color: T.gold }}>story</span>
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderRadius: 18, overflow: 'hidden', maxWidth: 1000, margin: '0 auto', width: '100%', background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)' }}>
              {[
                { val: `${clinics}+`, label: 'Clinics Onboarded' },
                { val: `${patients}+`, label: 'Patients Managed' },
                { val: `${speed}%`, label: 'Faster Documentation' },
                { val: '4.9/5', label: 'Average Client Rating' }
              ].map(({ val, label }, idx) => (
                <div key={label} style={{ padding: '44px 16px', borderRight: idx < 3 ? '1px solid rgba(255,255,255,0.1)' : 'none', textAlign: 'center' }}>
                  <div style={{ fontSize: 'clamp(1.8rem, 3vw, 3rem)', fontWeight: 700, color: '#fff', fontFamily: FONT_MONO, letterSpacing: '-1px', lineHeight: 1 }}>{val}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 14, textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 600 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ 20 WAYS WE TRANSFORM YOUR CLINIC — REDESIGNED ══ */}
      <section style={{ 
        padding: 'clamp(80px, 10vw, 140px) 5vw', 
        background: T.mist, 
        borderTop: `1px solid ${T.line}`, 
        position: 'relative', 
        overflow: 'hidden',
        minHeight: '100vh',
      }}>
        <img 
          src={IMG.happyDoctor} 
          alt="" 
          style={{ 
            position: 'absolute', 
            inset: 0, 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover', 
            objectPosition: 'center 30%',
            opacity: 0.05,
            pointerEvents: 'none',
          }} 
        />
        <AmbientBg light />
        <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 60, alignItems: 'start' }}>
            <div style={{ position: 'sticky', top: 100,  marginTop: 'clamp(1px, 0vw, 10px)', }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: T.brand, textTransform: 'uppercase', marginBottom: 14, fontFamily: FONT_MONO }}>Your Transformation</div>
              <h2 style={{ fontSize: 'clamp(1.7rem, 2.8vw, 2.3rem)', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-1px', margin: '0 0 18px', fontFamily: FONT_DISPLAY, color: T.ink }}>
                <span style={{ color: T.brand }}>20 Ways</span><br />We Transform Your Clinic
              </h2>
              <p style={{ color: T.slate, fontSize: 14.5, lineHeight: 1.75, marginBottom: 26 }}>
                From chaotic waiting rooms to seamless operations, from stressed staff to happy teams — see how your clinic becomes a better place.
              </p>
              <PrimaryButton onClick={openDemo} small className="desktop-only-demo-btn"><Icon path={ICONS.whatsapp} size={15} color="#fff" /> Book a Demo</PrimaryButton>
            </div>

            <div ref={valRef} style={{
              background: T.paper,
              borderRadius: 0,
              padding: 'clamp(28px, 4vw, 40px)',
              marginTop: 'clamp(60px, 12vw, 100px)', 
              border: `1px solid ${T.line}`,
              boxShadow: T.shadow,
              transition: 'all 0.4s',
              opacity: valInView ? 1 : 0,
              transform: valInView ? 'translateY(0)' : 'translateY(40px)',
              transition: `all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)`,
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = T.shadowHover; e.currentTarget.style.borderColor = T.signal; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = T.shadow; e.currentTarget.style.borderColor = T.line; }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '4px',
              }}>
                {TRANSFORMATIONS.map((v, i) => (
                  <ValueItem key={i} text={v} index={i} inView={valInView} />
                ))}
              </div>
              <div style={{ 
                textAlign: 'center', 
                marginTop: 20, 
                paddingTop: 16, 
                borderTop: `1px solid ${T.line}`,
                fontSize: 13,
                color: T.slate,
                fontWeight: 500,
              }}>
                <span style={{ color: T.brand, fontWeight: 700 }}>20</span> transformations that make clinics better places to work and heal
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* ══ TESTIMONIALS ══ */}
      <section style={{ padding: 'clamp(80px, 10vw, 120px) 5vw', background: T.paper, borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: T.brand, textTransform: 'uppercase', marginBottom: 14, fontFamily: FONT_MONO }}>Reviews</div>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.2vw, 2.6rem)', fontWeight: 700, letterSpacing: '-1px', fontFamily: FONT_DISPLAY, color: T.ink }}>
              Loved by clinics across <span style={{ color: T.brand }}>Uganda</span>
            </h2>
          </div>

          <div style={{ background: T.mist, borderRadius: 20, padding: 'clamp(32px, 5vw, 54px)', marginBottom: 28, position: 'relative', overflow: 'hidden', border: `1px solid ${T.line}` }}>
            <Icon path={ICONS.quote} size={46} color={T.brandPale} />
            <p style={{ fontSize: 'clamp(1rem, 1.8vw, 1.2rem)', color: T.ink, lineHeight: 1.75, margin: '18px 0 32px', fontFamily: FONT_DISPLAY, fontStyle: 'italic', fontWeight: 500, position: 'relative', zIndex: 1, maxWidth: 700 }}>
              "{TESTIMONIALS[activeT].text}"
            </p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 46, height: 46, borderRadius: '50%', background: `linear-gradient(135deg, ${T.brandLt}, ${T.brand})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: '#fff', fontFamily: FONT_DISPLAY }}>{TESTIMONIALS[activeT].initial}</div>
                <div>
                  <div style={{ fontWeight: 700, color: T.ink, fontSize: 14.5, fontFamily: FONT_DISPLAY }}>{TESTIMONIALS[activeT].name}</div>
                  <div style={{ fontSize: 12.5, color: T.brand, marginTop: 2 }}>{TESTIMONIALS[activeT].role}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {TESTIMONIALS.map((_, i) => (
                  <button key={i} onClick={() => setActiveT(i)} style={{ width: i === activeT ? 26 : 7, height: 7, borderRadius: 4, border: 'none', cursor: 'pointer', background: i === activeT ? T.brand : T.line, transition: 'all 0.3s' }} />
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} onClick={() => setActiveT(i)}
                style={{ background: T.mist, borderRadius: 16, padding: '26px 22px', border: `1.5px solid ${i === activeT ? T.brand : T.line}`, cursor: 'pointer', transition: 'all 0.3s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.brand; e.currentTarget.style.boxShadow = T.shadowHover; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = i === activeT ? T.brand : T.line; e.currentTarget.style.boxShadow = 'none'; }}>
                <div style={{ display: 'flex', gap: 3, marginBottom: 14 }}>
                  {[...Array(5)].map((_, si) => <Icon key={si} path={ICONS.star} size={13} color={T.gold} />)}
                </div>
                <p style={{ fontSize: 13.5, color: T.slate, lineHeight: 1.7, marginBottom: 18, fontStyle: 'italic' }}>"{t.text}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg, ${T.brand}, ${T.brandLt})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#fff', flexShrink: 0, fontFamily: FONT_DISPLAY }}>{t.initial}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13.5, color: T.ink, fontFamily: FONT_DISPLAY }}>{t.name}</div>
                    <div style={{ fontSize: 11.5, color: T.brand, marginTop: 2 }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FAQ ══ */}
      <section style={{ padding: 'clamp(80px, 10vw, 120px) 5vw', background: T.mist }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: T.brand, textTransform: 'uppercase', marginBottom: 14, fontFamily: FONT_MONO }}>Questions</div>
            <h2 style={{ fontSize: 'clamp(1.7rem, 3vw, 2.4rem)', fontWeight: 700, letterSpacing: '-1px', margin: 0, fontFamily: FONT_DISPLAY, color: T.ink }}>Before you <span style={{ color: T.brand }}>switch</span></h2>
          </div>
          <div style={{ background: T.paper, border: `1px solid ${T.line}`, borderRadius: 16, overflow: 'hidden', boxShadow: T.shadow }}>
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return <FAQItem key={i} q={f.q} a={f.a} open={isOpen} onToggle={() => setOpenFaq(isOpen ? -1 : i)} />;
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24, marginTop: 36, flexWrap: 'wrap' }}>
            {[['Encrypted & role-based access', 'shield'], ['Works on low bandwidth', 'wifi'], ['Live in under 10 minutes', 'clock']].map(([text, icon]) => (
              <span key={text} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: T.slate }}>
                <Icon path={ICONS[icon]} size={14} color={T.brand} /> {text}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section style={{ position: 'relative', overflow: 'hidden' }}>
        <img src={IMG.laptopDesk} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${T.brand}, ${T.brandLt})`, opacity: 0.92 }} />
        <div style={{ position: 'relative', zIndex: 1, padding: 'clamp(80px, 11vw, 120px) 5vw', textAlign: 'center' }}>
          <AmbientBg />
          <div style={{ position: 'relative', zIndex: 1, maxWidth: 680, margin: '0 auto' }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <Icon path={ICONS.activity} size={28} color="#fff" />
            </div>
            <h2 style={{ fontSize: 'clamp(1.9rem, 3.6vw, 3.1rem)', fontWeight: 700, color: '#fff', letterSpacing: '-1px', margin: '0 0 16px', fontFamily: FONT_DISPLAY }}>
              Ready to manage your clinic <span style={{ color: T.gold }}>better</span>?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15.5, marginBottom: 40, lineHeight: 1.75 }}>
              Join hundreds of clinics across Uganda already running on ClinicProSystem. It's affordable.
            </p>
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={register}
                style={{ padding: '16px 34px', borderRadius: 10, border: 'none', background: '#fff', color: T.brand, fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT_BODY, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.2)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.15)'; }}>
                <Icon path={ICONS.arrow} size={17} color={T.brand} /> Start Now
              </button>
              <button onClick={openDemo}
                style={{ padding: '16px 30px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.1)', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT_BODY, backdropFilter: 'blur(10px)', transition: 'all 0.3s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                <Icon path={ICONS.whatsapp} size={17} color="#fff" /> Chat on WhatsApp
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer style={{ background: T.canvas, padding: 'clamp(56px, 7vw, 84px) 5vw 96px', borderTop: `1px solid ${T.line}` }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 44, paddingBottom: 48, borderBottom: `1px solid ${T.line}` }}>
            <div style={{ flex: '1 1 240px', maxWidth: 300, textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 6 }}>
                <CPSLogo size={32} dark />
                <span style={{ fontSize: 17, fontWeight: 700, color: T.ink, fontFamily: FONT_DISPLAY }}>ClinicPro<span style={{ color: T.brand }}>System</span></span>
              </div>
              <div style={{ fontSize: 10, color: T.slate, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 14, fontFamily: FONT_MONO }}>Care. Smart. Digital.</div>
              <p style={{ fontSize: 13.5, color: T.slate, lineHeight: 1.8 }}>Modern AI-powered clinic management. Built for Uganda, trusted by hundreds of healthcare facilities.</p>
            </div>
            <div style={{ flex: '1 1 160px', maxWidth: 200, textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2.5, color: T.brand, textTransform: 'uppercase', marginBottom: 18, fontFamily: FONT_MONO }}>Quick Links</div>
              <button onClick={register} style={{ display: 'block', width: '100%', textAlign: 'center', fontSize: 13.5, color: T.slate, background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 12px', transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = T.brand}
                onMouseLeave={e => e.currentTarget.style.color = T.slate}>Register</button>
            </div>
            <div style={{ flex: '1 1 200px', maxWidth: 260, textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2.5, color: T.brand, textTransform: 'uppercase', marginBottom: 18, fontFamily: FONT_MONO }}>Contact Us</div>
              <p style={{ fontSize: 13.5, color: T.slate, lineHeight: 2.1 }}>+256 703 521 101<br />+256 786 747 733<br />clinicproug@gmail.com</p>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
                <button onClick={openDemo}
                  style={{ padding: '11px 22px', borderRadius: 9, border: 'none', background: `linear-gradient(135deg, ${T.brand}, ${T.brandLt})`, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.3s', boxShadow: '0 2px 20px rgba(15, 42, 92, 0.2)' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 4px 30px rgba(15, 42, 92, 0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 20px rgba(15, 42, 92, 0.2)'; }}>
                  <Icon path={ICONS.whatsapp} size={15} color="#fff" /> Chat on WhatsApp
                </button>
              </div>
            </div>
          </div>
          
          <div style={{ 
            paddingTop: 26, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            borderTop: `1px solid ${T.line}`,
            marginTop: 28,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
              <span style={{ fontSize: 12.5, color: T.slate }}>© 2026 ClinicProSystem. All rights reserved.</span>
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: T.slate }} />
              <span style={{ fontSize: 12.5, color: T.slate }}>Built with care in Uganda</span>
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 8,
              background: T.mist,
              padding: '10px 20px',
              borderRadius: 12,
              border: `1px solid ${T.line}`,
            }}>
              <Icon path={ICONS.building} size={16} color={T.brand} />
              <span style={{ fontSize: 12, color: T.slate, fontWeight: 500 }}>
                Developed, Managed and Mantained by <span style={{ color: T.brand, fontWeight: 700 }}>GOLD-QUEST E-SYSTEMS</span>
              </span>
              <Icon path={ICONS.code} size={14} color={T.signal} />
            </div>
          </div>
        </div>
      </footer>

      {/* ══ STICKY MOBILE CTA ══ */}
      <div className="mobile-sticky-cta" style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 300,
        display: 'none', gap: 10, padding: '12px 16px',
        background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(14px)',
        borderTop: `1px solid ${T.line}`, boxShadow: '0 -8px 30px rgba(10,22,40,0.08)',
      }}>
        <button onClick={openDemo} style={{ flex: 1, padding: '13px 10px', borderRadius: 10, border: `1.5px solid ${T.lineFirm}`, background: '#fff', color: T.ink, fontWeight: 700, fontSize: 13.5, fontFamily: FONT_BODY, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <Icon path={ICONS.whatsapp} size={15} color={T.signal} /> Demo
        </button>
        <button onClick={register} style={{ flex: 1.4, padding: '13px 10px', borderRadius: 10, border: 'none', background: `linear-gradient(135deg, ${T.brand}, ${T.brandLt})`, color: '#fff', fontWeight: 700, fontSize: 13.5, fontFamily: FONT_BODY, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <Icon path={ICONS.arrow} size={15} color="#fff" /> Register Free
        </button>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,500;1,9..144,600;1,9..144,700&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { 
          background: #F6F8FC; 
          margin: 0;
          padding: 0;
          -webkit-tap-highlight-color: transparent;
        }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: #E7EDF9; }
        ::-webkit-scrollbar-thumb { background: #2452A0; border-radius: 3px; }
        button:focus-visible, a:focus-visible { outline: 2px solid #0EA383; outline-offset: 2px; }

        @keyframes pulseDot { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.5; transform:scale(1.6); } }
        @keyframes slideDown { from { transform:translateY(-40px); opacity:0; } to { transform:translateY(0); opacity:1; } }

        .particle-field { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
        .particle { position: absolute; border-radius: 50%; animation: floatParticle linear infinite; }
        @keyframes floatParticle {
          0% { transform: translateY(0) translateX(0) scale(1); opacity: 0.2; }
          20% { opacity: 0.6; }
          80% { opacity: 0.6; }
          100% { transform: translateY(-180px) translateX(40px) scale(0.6); opacity: 0; }
        }

        @media (max-width: 720px) {
          .mobile-hamburger {
            display: flex !important;
          }
          nav {
            display: none !important;
          }
        }

        @media (min-width: 721px) {
          .mobile-hamburger {
            display: none !important;
          }
        }

        @media (max-width: 768px) {
          .desktop-only-demo-btn {
            display: none !important;
          }
        }

        @media (max-width: 992px) {
          section [style*="grid-template-columns: 1fr 1fr"] { 
            grid-template-columns: 1fr !important; 
          }
          section [style*="grid-template-columns: 1fr 2fr"] { 
            grid-template-columns: 1fr !important; 
          }
          section [style*="grid-template-columns: 1.1fr 0.9fr"] { 
            grid-template-columns: 1fr !important; 
          }
          section [style*="grid-template-columns: repeat(3, 1fr)"]:not([data-keep]) { 
            grid-template-columns: 1fr !important; 
          }
          .value-list-grid {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 768px) {
          section [style*="grid-template-columns: repeat(4,1fr)"],
          section [style*="grid-template-columns: repeat(4, 1fr)"] { 
            grid-template-columns: repeat(2,1fr) !important; 
          }
          section [style*="grid-template-columns: repeat(3,1fr)"]:not([data-keep]) { 
            grid-template-columns: 1fr !important; 
          }
          section [style*="grid-template-columns: repeat(auto-fit, minmax(230px, 1fr))"] { 
            grid-template-columns: 1fr !important; 
          }
          section [style*="grid-template-columns: 1fr 1fr"][data-mobile-stack] {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 480px) {
          section [style*="grid-template-columns: repeat(2,1fr)"] { 
            grid-template-columns: 1fr !important; 
          }
          section [style*="grid-template-columns: repeat(3, 1fr)"]:not([data-keep]) { 
            grid-template-columns: 1fr !important; 
          }
        }

        @media (max-width: 720px) {
          .mobile-sticky-cta { display: flex !important; }
          footer { padding-bottom: 96px; }
        }

        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
        }

        @media (max-width: 600px) {
          .hero-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
};

// ─── Helmet component for meta tags ──────────────────────────────────────
const Helmet = ({ children }) => {
  useEffect(() => {
    const metaTags = [];
    const existingMeta = document.querySelectorAll('meta[name="theme-color"], meta[name="apple-mobile-web-app-capable"], meta[name="apple-mobile-web-app-status-bar-style"]');
    existingMeta.forEach(el => el.remove());
    
    const metaConfigs = [
      { name: 'theme-color', content: '#FFFFFF' },
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'white' }
    ];
    
    metaConfigs.forEach(config => {
      const meta = document.createElement('meta');
      meta.name = config.name;
      meta.content = config.content;
      document.head.appendChild(meta);
      metaTags.push(meta);
    });
    
    return () => {
      metaTags.forEach(el => el.remove());
    };
  }, []);
  
  return null;
};

export default LandingPage;